import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    ICompiledSQLTokens,
    generateTableFullPath,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardCampaignsDatasource } from '../../mopa-campaigns/sql-maps/biviz-dashboard-campaigns-sql-map';
export interface CampaignsNetworkWarningDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class CampaignsNetworkWarningDataLoaderFactory extends Factory<
    DataLoader,
    CampaignsNetworkWarningDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'CampaignsNetworkWarningDataLoaderFactory';

    getInstance(options: CampaignsNetworkWarningDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                const campaignMastertableDatasource =
                    BiVizDashboardCampaignsDatasource;

                const campaignsMastertableSqlMap =
                    campaignMastertableDatasource.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    campaignsMastertableSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const activeAll =
                    queryContext.settings[
                        QueryContextSettingID.CAMPAIGN_ACTIVE_ALL
                    ] ?? 'all';
                const activeCampaignSQL: Partial<ICompiledSQLTokens> =
                    activeAll === 'all'
                        ? {}
                        : {
                              where: [`active_campaign IS TRUE`],
                          };

                const campaignsMastertableSql =
                    campaignMastertableDatasource.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options,
                        mergeSqlTokensArray([activeCampaignSQL])
                    );
                const query = `
-- DataLoaderId: ${this.instanceId}

SELECT
  is_warning
  , networks
  , IF (is_warning, CONCAT('Charts use iOS data from ', networks, ' networks, which are not fully attributted.'), '') AS message
FROM
(
  SELECT COUNT(*) > 0 AS is_warning
  , ARRAY_TO_STRING(ARRAY_AGG(DISTINCT unified_network ORDER BY unified_network), ', ') AS networks
  FROM
  (
    SELECT DISTINCT first_store, unified_network
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        campaignMastertableDatasource.sqlMap.dataset,
        campaignMastertableDatasource.sqlMap.tableId
    )}\` ds
    ${campaignsMastertableSql.where}
  )
  WHERE first_store = 'App Store' AND unified_network IN ('Google Ads', 'Facebook')
)

`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
